import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Row,
  Col,
  Card,
  Badge,
  Alert,
  Table,
  ProgressBar,
  Button,
  Form,
} from "react-bootstrap";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line, Doughnut } from "react-chartjs-2";
import SectionContainer from "../../components/UI/SectionContainer";
import Spinner from "../../components/UI/Spinner";
import dashboardService from "../../services/dashboardService";
import { dateFormat } from "../../utils/common";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const DashBoard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const intervalRef = useRef(null);

  // 데이터 로드
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [dashResult, userStats] = await Promise.all([
        dashboardService.getDashboardData(),
        dashboardService.getUserStatistics(),
      ]);

      if (dashResult.success) {
        setDashboardData({
          ...dashResult.data,
          userStats,
        });
        setLastUpdated(new Date());
      } else {
        setError(dashResult.message || "데이터를 불러오는데 실패했습니다.");
      }
    } catch (err) {
      setError("대시보드 데이터를 불러오는 중 오류가 발생했습니다.");
      console.error("Dashboard load error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // 초기 로드 및 자동 새로고침
  useEffect(() => {
    loadData();

    if (autoRefresh) {
      intervalRef.current = setInterval(loadData, 60000); // 1분마다 새로고침
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [loadData, autoRefresh]);

  // 시간 포맷
  const formatTime = (timestamp) => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return `${diffInSeconds}초 전`;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}분 전`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)}시간 전`;
    return dateFormat(timestamp);
  };

  // 알림 우선순위 Badge (display_priority: 1=높음, 2=보통, 3=낮음)
  const getPriorityBadge = (priority) => {
    const variants = {
      1: "danger",
      2: "warning",
      3: "info",
      high: "danger",
      medium: "warning",
      low: "info",
    };
    const labels = {
      1: "높음",
      2: "보통",
      3: "낮음",
      high: "높음",
      medium: "보통",
      low: "낮음",
    };
    return (
      <Badge bg={variants[priority] || "secondary"}>
        {labels[priority] || priority}
      </Badge>
    );
  };

  // 알림 타입 아이콘
  const getAlertTypeIcon = (type) => {
    const icons = {
      security_notice: "🔒",
      maintenance_alert: "🔧",
      login_banner: "📝",
      update: "📦",
      general: "ℹ️",
    };
    return icons[type] || "📢";
  };

  // 보안 이벤트 심각도 Badge
  const getSeverityBadge = (severity) => {
    const variants = {
      CRITICAL: "danger",
      HIGH: "warning",
      MEDIUM: "info",
      LOW: "secondary",
    };
    return <Badge bg={variants[severity] || "secondary"}>{severity}</Badge>;
  };

  // 활동 추이 차트 데이터
  const getActivityChartData = () => {
    // API는 배열을 직접 반환
    const timeline = dashboardData?.auditTimeline;
    if (!timeline || !Array.isArray(timeline) || timeline.length === 0)
      return null;

    return {
      labels: timeline.map((item) => {
        const date = new Date(item.time_bucket || item.date || item.period);
        return `${date.getMonth() + 1}/${date.getDate()}`;
      }),
      datasets: [
        {
          label: "활동 건수",
          data: timeline.map(
            (item) =>
              parseInt(item.success_count || 0) +
              parseInt(item.failure_count || 0)
          ),
          borderColor: "#0d6efd",
          backgroundColor: "rgba(13, 110, 253, 0.1)",
          fill: true,
          tension: 0.3,
        },
      ],
    };
  };

  // 카테고리 분포 차트 데이터
  const getCategoryChartData = () => {
    // API는 배열을 직접 반환
    const categories = dashboardData?.auditCategory;
    if (!categories || !Array.isArray(categories) || categories.length === 0)
      return null;
    const colors = [
      "#0d6efd",
      "#198754",
      "#ffc107",
      "#dc3545",
      "#6c757d",
      "#0dcaf0",
    ];

    // 카테고리 라벨 변환
    const getCategoryLabel = (category) => {
      const labels = {
        ACCESS_CONTROL: "접근 제어",
        DATA_PROTECTION: "데이터 보호",
        SYSTEM_SECURITY: "시스템 보안",
        AUDIT_MONITORING: "감사 모니터링",
        USER_MANAGEMENT: "사용자 관리",
        BUSINESS_LOGIC: "비즈니스 로직",
        SECURITY_VIOLATION: "보안 위반",
        BIOMETRIC_AUTH: "생체 인증",
        MEDICAL_DATA: "의료 데이터",
        BIOMETRIC_MANAGEMENT: "생체정보 관리",
        SYSTEM_MANAGEMENT: "시스템 관리",
      };
      return labels[category] || category;
    };

    return {
      labels: categories.map((cat) => getCategoryLabel(cat.category)),
      datasets: [
        {
          data: categories.map((cat) =>
            parseInt(cat.event_count || cat.count || 0)
          ),
          backgroundColor: colors.slice(0, categories.length),
          borderWidth: 0,
        },
      ],
    };
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
    },
    scales: {
      y: { beginAtZero: true },
    },
  };

  const doughnutChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "right",
        labels: { boxWidth: 12, padding: 15 },
      },
    },
  };

  if (loading && !dashboardData) {
    return (
      <SectionContainer title="대시보드">
        <div className="text-center py-5">
          <Spinner visible={true} />
        </div>
      </SectionContainer>
    );
  }

  // API 응답 구조에 맞게 통계 데이터 추출
  const sessionStats = dashboardData?.sessionStats || {};
  const userSessionStats = sessionStats.user_sessions || {};
  const medicalBypassStats = sessionStats.medical_bypass || {};
  const userStats = dashboardData?.userStats || {};

  return (
    <SectionContainer title="대시보드">
      {/* 헤더 컨트롤 */}
      <Row className="mb-3 align-items-center">
        <Col>
          {lastUpdated && (
            <small className="text-muted">
              마지막 업데이트: {lastUpdated.toLocaleTimeString()}
            </small>
          )}
        </Col>
        <Col xs="auto" className="d-flex gap-2 align-items-center">
          <Form.Check
            type="switch"
            id="auto-refresh"
            label="자동 새로고침"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
          <Button
            variant="outline-primary"
            size="sm"
            onClick={loadData}
            disabled={loading}
          >
            {loading ? "로딩..." : "새로고침"}
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      {/* 통계 카드 */}
      <Row className="mb-4">
        <Col md={3} sm={6} className="mb-3">
          <Card className="h-100 border-primary">
            <Card.Body className="text-center">
              <div className="text-muted small mb-1">👥 활성 세션</div>
              <h2 className="mb-0 text-primary">
                {userSessionStats.summary?.total_active || 0}
              </h2>
              <small className="text-muted">
                의료진: {medicalBypassStats.active_count || 0}
              </small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6} className="mb-3">
          <Card className="h-100 border-success">
            <Card.Body className="text-center">
              <div className="text-muted small mb-1">📊 오늘 로그인</div>
              <h2 className="mb-0 text-success">
                {userSessionStats.today?.new_sessions || 0}
              </h2>
              <small className="text-muted">
                강제종료: {userSessionStats.today?.forced_terminations || 0}
              </small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6} className="mb-3">
          <Card className="h-100 border-info">
            <Card.Body className="text-center">
              <div className="text-muted small mb-1">👤 총 사용자</div>
              <h2 className="mb-0 text-info">{userStats.totalUsers || 0}</h2>
              <small className="text-muted">정상 계정</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6} className="mb-3">
          <Card className="h-100 border-warning">
            <Card.Body className="text-center">
              <div className="text-muted small mb-1">⚠️ 보안 이벤트</div>
              <h2 className="mb-0 text-warning">
                {dashboardData?.alertOverview?.summary?.security_events_today ||
                  0}
              </h2>
              <small className="text-muted">24시간 기준</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 시스템 알림 & 공지사항 */}
      <Row className="mb-4">
        <Col md={6} className="mb-3">
          <Card className="h-100">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <strong>📢 시스템 알림</strong>
              <Badge bg="primary" pill>
                {dashboardData?.systemAlerts?.length || 0}
              </Badge>
            </Card.Header>
            <Card.Body className="p-0">
              {dashboardData?.systemAlerts?.length > 0 ? (
                <Table hover className="mb-0">
                  <tbody>
                    {dashboardData.systemAlerts
                      .slice(0, 5)
                      .map((alert, index) => (
                        <tr key={alert.id || index}>
                          <td style={{ width: "30px" }}>
                            {getAlertTypeIcon(alert.message_type)}
                          </td>
                          <td>
                            <div className="fw-semibold">{alert.title}</div>
                            <small className="text-muted">
                              {alert.message?.substring(0, 50)}
                              {alert.message?.length > 50 ? "..." : ""}
                            </small>
                          </td>
                          <td style={{ width: "60px" }} className="text-end">
                            {getPriorityBadge(alert.display_priority)}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              ) : (
                <div className="text-center text-muted py-4">
                  활성화된 시스템 알림이 없습니다.
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} className="mb-3">
          <Card className="h-100">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <strong>📋 최근 공지사항</strong>
              <Badge bg="secondary" pill>
                {dashboardData?.notices?.length || 0}
              </Badge>
            </Card.Header>
            <Card.Body className="p-0">
              {dashboardData?.notices?.length > 0 ? (
                <Table hover className="mb-0">
                  <tbody>
                    {dashboardData.notices.slice(0, 5).map((notice, index) => (
                      <tr key={notice.idx || index}>
                        <td>
                          <div className="fw-semibold">
                            {notice.title_ko || notice.title || "(제목 없음)"}
                          </div>
                          <small className="text-muted">
                            {dateFormat(notice.regdate || notice.update_date)}
                          </small>
                        </td>
                        <td style={{ width: "80px" }} className="text-end">
                          <small className="text-muted">
                            {notice.update_date ? "수정됨" : ""}
                          </small>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <div className="text-center text-muted py-4">
                  등록된 공지사항이 없습니다.
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 활동 추이 차트 */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header>
              <strong>📈 최근 7일 활동 추이</strong>
            </Card.Header>
            <Card.Body>
              {getActivityChartData() ? (
                <div style={{ height: "250px" }}>
                  <Line
                    data={getActivityChartData()}
                    options={lineChartOptions}
                  />
                </div>
              ) : (
                <div className="text-center text-muted py-4">
                  활동 데이터가 없습니다.
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 보안 이벤트 & 카테고리 분포 */}
      <Row className="mb-4">
        <Col md={7} className="mb-3">
          <Card className="h-100">
            <Card.Header>
              <strong>📋 최근 감사 이벤트</strong>
            </Card.Header>
            <Card.Body className="p-0">
              {dashboardData?.securityEvents?.length > 0 ? (
                <Table responsive hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>시간</th>
                      <th>이벤트</th>
                      <th>심각도</th>
                      <th>사용자</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.securityEvents
                      .slice(0, 5)
                      .map((event, index) => (
                        <tr key={event.id || index}>
                          <td>
                            <small>
                              {formatTime(event.created_at || event.timestamp)}
                            </small>
                          </td>
                          <td>
                            <small>
                              {event.activity_type || event.event_type || "-"}
                            </small>
                          </td>
                          <td>
                            {getSeverityBadge(
                              event.severity || event.risk_level || "LOW"
                            )}
                          </td>
                          <td>
                            <small className="text-muted">
                              {event.user_id || event.actor_id || "-"}
                            </small>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              ) : (
                <div className="text-center text-muted py-4">
                  최근 감사 이벤트가 없습니다.
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={5} className="mb-3">
          <Card className="h-100">
            <Card.Header>
              <strong>📊 감사로그 카테고리 분포</strong>
            </Card.Header>
            <Card.Body>
              {getCategoryChartData() ? (
                <div style={{ height: "200px" }}>
                  <Doughnut
                    data={getCategoryChartData()}
                    options={doughnutChartOptions}
                  />
                </div>
              ) : (
                <div className="text-center text-muted py-4">
                  카테고리 데이터가 없습니다.
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 세션 상세 정보 */}
      <Row>
        <Col md={6} className="mb-3">
          <Card>
            <Card.Header>
              <strong>👥 사용자 세션 현황</strong>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <small>활성 세션</small>
                  <small>{userSessionStats.summary?.total_active || 0}개</small>
                </div>
                <ProgressBar
                  now={Math.min(
                    ((userSessionStats.summary?.total_active || 0) / 100) * 100,
                    100
                  )}
                  variant="primary"
                />
              </div>
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <small>오늘 만료</small>
                  <small>{userSessionStats.today?.expired || 0}개</small>
                </div>
                <ProgressBar
                  now={Math.min(
                    ((userSessionStats.today?.expired || 0) / 50) * 100,
                    100
                  )}
                  variant="warning"
                />
              </div>
              <div className="d-flex justify-content-between">
                <small className="text-muted">오늘 신규 세션</small>
                <small>{userSessionStats.today?.new_sessions || 0}개</small>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} className="mb-3">
          <Card>
            <Card.Header>
              <strong>🏥 의료진 우회 세션</strong>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <small>활성 세션</small>
                  <small>
                    {sessionStats.medical_bypass_sessions?.active || 0}개
                  </small>
                </div>
                <ProgressBar
                  now={Math.min(
                    ((sessionStats.medical_bypass_sessions?.active || 0) / 20) *
                    100,
                    100
                  )}
                  variant="success"
                />
              </div>
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <small>오늘 생성</small>
                  <small>
                    {sessionStats.medical_bypass_sessions?.created_today || 0}개
                  </small>
                </div>
                <ProgressBar
                  now={Math.min(
                    ((sessionStats.medical_bypass_sessions?.created_today ||
                      0) /
                      10) *
                    100,
                    100
                  )}
                  variant="info"
                />
              </div>
              <div className="d-flex justify-content-between">
                <small className="text-muted">만료된 세션 (오늘)</small>
                <small>
                  {sessionStats.medical_bypass_sessions?.expired_today || 0}개
                </small>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </SectionContainer>
  );
};

export default DashBoard;
